import type { NetStatusSnapshot } from '@md/shared';
import { button, el } from '../ui/dom';
import { createQrSvg, joinUrl } from '../ui/qr';

/**
 * Écran de LOBBY (concept §6.2, spec §2) : code de partie + QR code.
 *
 * L'hôte y attend son partenaire ; le second joueur y arrive après avoir saisi
 * le code (ou scanné le QR, qui pointe sur `/?join=CODE`).
 *
 * v1.0.1 — l'écran dit explicitement qu'il ATTEND (aucun bouton « démarrer »
 * n'existe à ce stade : la suite est automatique dès que l'autre arrive), et
 * propose les deux gestes de partage du téléphone : `navigator.share` quand le
 * système le fournit, et la copie du lien dans le presse-papier sinon.
 */
export interface LobbyScreen {
  readonly element: HTMLElement;
  update(status: NetStatusSnapshot): void;
}

export interface LobbyScreenOptions {
  readonly onLeave: () => void;
  /** Origine utilisée pour le lien d'invitation (injectable pour les tests). */
  readonly origin?: string;
  /** Partage natif — `navigator.share` par défaut, absent sur bureau. */
  readonly share?: ((data: { title?: string; text?: string; url: string }) => Promise<void>) | null;
  /** Écriture presse-papier — `navigator.clipboard.writeText` par défaut. */
  readonly writeText?: ((text: string) => Promise<void>) | null;
}

/** Durée d'affichage du retour « Copié ! », en millisecondes. */
const COPY_FEEDBACK_MS = 1800;

/**
 * Repli de copie pour les navigateurs sans presse-papier asynchrone (ou sans
 * permission) : sélection d'un champ hors écran + `execCommand('copy')`.
 */
function copyBySelection(text: string): boolean {
  const field = document.createElement('textarea');
  field.value = text;
  field.setAttribute('readonly', '');
  field.style.position = 'fixed';
  field.style.top = '-1000px';
  field.style.opacity = '0';
  document.body.append(field);
  field.select();
  field.setSelectionRange(0, text.length);
  let copied = false;
  try {
    // `execCommand` est obsolète mais reste le seul repli universel.
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  }
  field.remove();
  return copied;
}

export function createLobbyScreen(options: LobbyScreenOptions): LobbyScreen {
  const view = el('main', 'screen screen--lobby');

  const title = el('h1', 'lobby__title', 'Partie en réseau');
  const code = el('div', 'lobby__code');
  code.dataset.testid = 'room-code';

  const qr = el('div', 'lobby__qr');
  qr.dataset.testid = 'room-qr';

  const url = el('a', 'lobby__url');
  url.dataset.testid = 'room-url';
  url.rel = 'noreferrer';

  const players = el('ul', 'lobby__players');
  players.dataset.testid = 'lobby-players';

  const hint = el('p', 'lobby__hint');
  hint.dataset.testid = 'lobby-hint';

  const waiting = el('p', 'lobby__waiting');
  waiting.dataset.testid = 'lobby-waiting';
  const waitingText = el('span', 'lobby__waiting-text');
  waiting.append(el('span', 'lobby__waiting-dot'), waitingText);

  // --- Partage ------------------------------------------------------------
  const share =
    options.share !== undefined
      ? options.share
      : typeof navigator !== 'undefined' && typeof navigator.share === 'function'
        ? (data: { title?: string; text?: string; url: string }) => navigator.share(data)
        : null;
  const writeText =
    options.writeText !== undefined
      ? options.writeText
      : typeof navigator !== 'undefined' && navigator.clipboard
        ? (text: string) => navigator.clipboard.writeText(text)
        : null;

  const actions = el('div', 'lobby__actions');
  actions.dataset.testid = 'lobby-actions';

  const shareButton = button('Partager', 'share-link', 'button button--share');
  const copyButton = button('Copier le lien', 'copy-link', 'button button--ghost');
  const copyLabel = copyButton.textContent ?? 'Copier le lien';
  let copyTimer: number | undefined;

  let link = '';

  const flashCopy = (ok: boolean): void => {
    copyButton.textContent = ok ? 'Copié !' : 'Copie impossible';
    copyButton.dataset.state = ok ? 'copied' : 'failed';
    if (copyTimer !== undefined) {
      window.clearTimeout(copyTimer);
    }
    copyTimer = window.setTimeout(() => {
      copyButton.textContent = copyLabel;
      delete copyButton.dataset.state;
    }, COPY_FEEDBACK_MS);
  };

  copyButton.addEventListener('click', () => {
    if (!link) {
      return;
    }
    if (!writeText) {
      flashCopy(copyBySelection(link));
      return;
    }
    void writeText(link).then(
      () => flashCopy(true),
      () => {
        // Permission refusée ou contexte non sécurisé : repli sur la sélection.
        flashCopy(copyBySelection(link));
      },
    );
  });

  if (share) {
    shareButton.addEventListener('click', () => {
      if (!link) {
        return;
      }
      void share({
        title: 'Magnetic Duo',
        text: 'Rejoins ma partie de Magnetic Duo !',
        url: link,
      }).catch(() => {
        // Partage annulé par l'utilisateur : rien à signaler.
      });
    });
    actions.append(shareButton);
  }
  actions.append(copyButton);

  const leave = button('Quitter la partie', 'lobby-leave', 'button button--ghost');
  leave.addEventListener('click', () => options.onLeave());

  view.append(
    title,
    el('p', 'lobby__label', 'Code de la partie'),
    code,
    qr,
    url,
    actions,
    waiting,
    players,
    hint,
    leave,
  );

  let renderedCode: string | null = null;

  return {
    element: view,

    update(status: NetStatusSnapshot): void {
      const roomCode = status.roomCode ?? '';
      code.textContent = roomCode;
      code.dataset.code = roomCode;

      if (roomCode && roomCode !== renderedCode) {
        renderedCode = roomCode;
        link = joinUrl(roomCode, options.origin ?? window.location.origin);
        qr.replaceChildren(createQrSvg(link));
        url.textContent = link;
        url.href = link;
      }

      players.replaceChildren();
      for (const player of status.players) {
        const item = el('li', 'lobby__player');
        item.dataset.memberId = player.memberId;
        item.dataset.connected = String(player.connected);
        const who = player.memberId === status.memberId ? 'Vous' : 'Partenaire';
        item.textContent = `${who}${player.host ? ' (hôte)' : ''}${
          player.connected ? '' : ' — déconnecté'
        }`;
        players.append(item);
      }
      players.dataset.count = String(status.players.length);

      const alone = status.players.length < 2;
      hint.textContent = alone
        ? 'Partagez ce code (ou ce QR code) avec votre partenaire.'
        : 'Votre partenaire a rejoint la partie !';
      // Aucun bouton « démarrer » ici : dire ce que l'on attend évite de
      // chercher un bouton qui n'existe pas (retour d'usage v1.0.0).
      waitingText.textContent = alone
        ? 'En attente de l’autre joueur…'
        : 'Partenaire connecté — choix des robots…';
      waiting.dataset.state = alone ? 'waiting' : 'ready';
    },
  };
}
